import { MEDIA_TYPE, TRANSMISSION_TYPE } from './enums';
import { ISubtitleStyle } from './Subtitle';

export enum DatahubEntity {
  FIRSTPLAY = 'FIRSTPLAY',
  WATCHTIME = 'WATCHTIME',
  SUBTITLE = 'SUBTITLE',
  FULLSCREEN = 'FULLSCREEN',
  PLAYBACK_SPEED = 'PLAYBACK_SPEED',
  AUTOPLAY = 'AUTOPLAY',
  RESOLUTION = 'RESOLUTION',
  PLAYBACK = 'PLAYBACK',
  VOLUME = 'VOLUME',
  MARKER = 'MARKER',
  SEEKED = 'SEEKED',
  PICTURE_IN_PICTURE = 'PICTURE_IN_PICTURE',
  LOADEDMETADATA = 'LOADEDMETADATA',
  ERROR = 'ERROR',
  SUBTITLE_STYLE = 'SUBTITLE_STYLE',
  SCHEMA_VALIDATION_ERROR = 'SCHEMA_VALIDATION_ERROR',
  USER_EVENT_TRIGGER = 'USER_EVENT_TRIGGER',
}

export enum DATA_VERSION {
  '1.0' = '1.0',
  '2.0' = '2.0',
}

export type CaptureEventData = {
  event: IDefaultSchema | ISchemaValidationErrorSchema;
  data_version: DATA_VERSION;
};

export interface IDefaultEventParams {
  currentTime: number;
}

export interface IDefaultSchema {
  applicationKey: string;
  media: string;
  duration: number;
  user: string;
  viewId: string;
  playerVersion: string;
  experimentName?: string;
  transmissionType: TRANSMISSION_TYPE;
  mediaType: MEDIA_TYPE;
  isMediaPublic: boolean;
}

export interface IFirstplaySchema extends IDefaultSchema {
  mediaPosition: number;
}

export interface IWatchtimeSchema extends IDefaultSchema {
  viewId: string;
  startTime: number;
  endTime: number;
}

export interface ISubtitleSchema extends IDefaultSchema {
  language: string;
  status: string;
  time: number;
}
export interface IAutoplaySchema extends IDefaultSchema {
  status: string;
  time: number;
}
export interface IPlaybackSpeedSchema extends IDefaultSchema {
  speed: number;
  time: number;
}
export interface IFullscreenSchema extends IDefaultSchema {
  status: string;
  time: number;
}
export interface IResolutionSchema extends IDefaultSchema {
  resolution: string;
  time: number;
}
export interface IMarkerSchema extends IDefaultSchema {
  text: string;
  time: number;
}

export interface IPlaybackSchema extends IDefaultSchema {
  time: number;
}

export interface IVolumeSchema extends IDefaultSchema {
  volume: number;
  time: number;
}

export interface ISeekedSchema extends IDefaultSchema {
  startTime: number;
  endTime: number;
}

export interface IPictureInPictureSchema extends IDefaultSchema {
  status: string;
  time: number;
}

export interface IErrorSchema extends IDefaultSchema {
  code: string;
  time: number;
}

/*eslint-disable @typescript-eslint/no-empty-interface*/
export interface ILoadedMetadataSchema extends IDefaultSchema {}

export interface ISubtitleStyleSchema extends IDefaultSchema, ISubtitleStyle {}

export interface ISchemaValidationErrorSchema {
  entity: DatahubEntity;
  action: DatahubAction;
  errors: string;
  value: string;
}

export interface IUserEventTriggerSchema extends IDefaultSchema, IUserEventTrigger {}

export interface IUserEventTrigger {
  trigger: UserEventTriggerType;
  event: UserEventTriggerName;
}

export enum UserEventTriggerName {
  TOGGLE_AUTOPLAY = 'TOGGLE_AUTOPLAY',
  TOGGLE_FULLSCREEN = 'TOGGLE_FULLSCREEN',
  PAUSE = 'PAUSE',
  PLAY = 'PLAY',
  FORWARD_SECONDS = 'FORWARD_SECONDS',
  BACKWARD_SECONDS = 'BACKWARD_SECONDS',
  CHANGE_VOLUME = 'CHANGE_VOLUME',
  TOGGLE_SUBTITLE = 'TOGGLE_SUBTITLE',
  CHANGE_SUBTITLE_LANGUAGE = 'CHANGE_SUBTITLE_LANGUAGE',
  CHANGE_PLAYBACK_RATE = 'CHANGE_PLAYBACK_RATE',
  CHANGE_RESOLUTION = 'CHANGE_RESOLUTION',
  TOGGLE_PICTURE_IN_PICTURE = 'TOGGLE_PICTURE_IN_PICTURE',
}

export enum UserEventTriggerType {
  CLICK = 'click',
  SHORTCUT = 'shortcut',
}

export enum SubtitleStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum AutoplayStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum FullscreenStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum PictureInPictureStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export type DatahubAction = 'MEASURE' | 'CHANGE' | 'TOGGLE' | 'CREATE' | 'PLAY' | 'PAUSE';

export type CaptureEvent<ActionType> = (entity: DatahubEntity, action: ActionType, eventData: CaptureEventData) => void;

export enum FirstplayAction {
  MEASURE = 'MEASURE',
}

export enum LoadedMetadataAction {
  MEASURE = 'MEASURE',
}

export enum WatchtimeAction {
  MEASURE = 'MEASURE',
}

export enum SubtitleAction {
  CHANGE = 'CHANGE',
  TOGGLE = 'TOGGLE',
}

export enum AutoplayAction {
  TOGGLE = 'TOGGLE',
}

export enum PlaybackSpeedAction {
  CHANGE = 'CHANGE',
}

export enum FullscreenAction {
  CHANGE = 'CHANGE',
}

export enum ResolutionAction {
  CHANGE = 'CHANGE',
}

export enum PlaybackAction {
  PLAY = 'PLAY',
  PAUSE = 'PAUSE',
}

export enum VolumeAction {
  CHANGE = 'CHANGE',
}

export enum MarkerAction {
  CREATE = 'CREATE',
}

export enum SeekedAction {
  MEASURE = 'MEASURE',
}

export enum PictureInPictureAction {
  CHANGE = 'CHANGE',
}

export enum ErrorAction {
  MEASURE = 'MEASURE',
}

export enum SubtitleStyleAction {
  CHANGE = 'CHANGE',
}

export enum SchemaValidationErrorAction {
  CREATE = 'CREATE',
}

export enum UserEventTriggerAction {
  MEASURE = 'MEASURE',
}
